import * as PrefabManager from '../PrefabManager';
import * as EffectsComposer from '../../utils/EffectsComposer';

import {
    game
} from "../../Game";

import {
    Settings
} from "../../Settings";
import { b2SubVec2 } from '../../../libs/debugdraw';
import { hasDenseBlastNear, queueExplosion, rememberDenseBlast } from '../../utils/ExplosionManager';
import { recordReplayEvent } from '../../utils/ReplayExportJob';
import { scorchBlast } from '../../utils/paint/scorch';
import * as ExplosionEffects from '../../utils/ExplosionEffects';

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();

export class Explosive extends PrefabManager.weaponPrefab {
    constructor(target) {
		super(target);
		this.isExplosive = true;
    }
    init() {
		this.explosiveRadius = 250;
		this.explosionScale = 0.46;
		this.explosivePower = this.prefabObject.settings.force;
		this.explodeTimer = 0;
		this.explodeDelay = this.prefabObject.settings.delay*1000;
		this.explosiveRays = 20;
		this.explodeTarget = null;
		this.active = this.prefabObject.settings.active;
		this.activateOn = this.prefabObject.settings.activateOn;
		this.impactForExplosion = 10000;
		this.exploded = false;
		// this.clipWalls = false;
		// this.exploded = false;

        super.init();
	}
	explode(){
		queueExplosion(this);
	}
	performExplosion(){
		if(this.exploded || this.destroyed) return;
		this.exploded = true;

		const rayStartPosition = this.explodeTarget.GetPosition();
		recordReplayEvent('explosion', {radius: this.explosiveRadius}, rayStartPosition);
		const radius = this.explosiveRadius/Settings.PTM;
		const denseBlastAlreadyHandled = hasDenseBlastNear(rayStartPosition, radius);
		getBodies.clean();

		if(!denseBlastAlreadyHandled){
			const aabb = new Box2D.b2AABB();
			aabb.get_lowerBound().Set(rayStartPosition.x-radius, rayStartPosition.y-radius);
			aabb.get_upperBound().Set(rayStartPosition.x+radius, rayStartPosition.y+radius);
			this.explodeTarget.GetWorld().QueryAABB(getBodies, aabb);
			Box2D.destroy(aabb);
		}

		// A Box2D world raycast visits every overlapping dynamic fixture even though
		// explosions only use static fixtures as blockers. In a deliberately dense
		// pile this turns each blast into an O(n²) JS/WASM callback storm. Preserve
		// exact wall occlusion for normal blasts; once the query is abnormally dense,
		// use radial falloff and body centers so the work stays linear.
		const useDenseFallback = denseBlastAlreadyHandled
			|| getBodies.bodies.length > Settings.maxExplosionOcclusionBodies;
		this.denseExplosion = useDenseFallback;
		if(useDenseFallback && !denseBlastAlreadyHandled){
			rememberDenseBlast(rayStartPosition, radius);
		}
		for(const body of getBodies.bodies){
			const bodyPosition = body.GetPosition();
			let hitPoint;
			let fraction;

			if(useDenseFallback){
				const dx = bodyPosition.x - rayStartPosition.x;
				const dy = bodyPosition.y - rayStartPosition.y;
				fraction = Math.sqrt(dx * dx + dy * dy) / radius;
				if(fraction >= 1) continue;
				hitPoint = body.GetWorldCenter();
			}else{
				rayCallback.reset(body);
				this.explodeTarget.GetWorld().RayCast(rayCallback, rayStartPosition, bodyPosition);
				if(!rayCallback.m_hit || rayCallback.m_blockerFraction < rayCallback.m_fraction) continue;
				fraction = rayCallback.m_fraction;
				hitPoint = rayCallback.m_point;
			}

			const diff = vec1;
			diff.Set(rayStartPosition.x, rayStartPosition.y);
			b2SubVec2(diff, bodyPosition);
			diff.Normalize();

			const power = (1-fraction)*this.explosivePower*5;
			const force = vec2;
			const pushPower = power / game.editor.editorSettingsObject.gameSpeed;
			force.Set(pushPower*-diff.x, pushPower*-diff.y);

			body.ApplyForce(force, hitPoint, true);
			const powerRate = power/this.explosivePower;

			if(body.isFlesh){
				// A close blast bites a ragged chunk out; soot over it burns the edge and the skin around.
				if(powerRate > .2) self.editor.queueDecalToBody(body, hitPoint, "Decal.png", true, powerRate*3, Math.random()*Math.PI*2);
				self.editor.queueDecalToBody(body, hitPoint, "skorch.png", false, powerRate*5, Math.atan2(diff.y, diff.x), {burn:powerRate*.6});
			}

			if (powerRate > .2 && body.mySprite?.data?.prefabInstanceName) {
				const tarPrefab = game.editor.activePrefabs[body.mySprite.data.prefabInstanceName]?.class;

				if(tarPrefab?.isExplosive){
					tarPrefab.explode();
				}
				if(tarPrefab?.isTurret){
					tarPrefab.explosionHit(powerRate);
				}

				const bodyClass = game.editor.retrieveSubClassFromBody(body);
				if(bodyClass?.dealDamage){
					const slidingPowerScalar = 50;
					bodyClass.dealDamage(power/slidingPowerScalar);
				}
			}
		}

		scorchBlast(rayStartPosition.x, rayStartPosition.y, radius, this.explosiveRadius * 0.45, this.explodeTarget);

        const pixiPoint = game.editor.getPIXIPointFromWorldPoint(rayStartPosition);
		EffectsComposer.addEffect(EffectsComposer.effectTypes.shockWave, {radius:this.explosiveRadius*10, point:pixiPoint});
        EffectsComposer.addEffect(EffectsComposer.effectTypes.screenShake, {amplitude:this.explosivePower/200, point:pixiPoint});
		ExplosionEffects.play(this.explodeTarget, rayStartPosition, this.explosionScale, this.explosivePower);

	}
    update(){
		super.update();
		if(this.active){
			if (PrefabManager.timerReady(this.explodeTimer, this.explodeDelay, true)) {
				this.explode();
			}
			this.explodeTimer += game.editor.deltaTime;
		}
	}
	set(property, value) {
        switch (property) {
			case 'active':
				if(!this.active){
					this.explodeTimer = 0;
					this.active = true;
				}
            default:
                this.prefabObject.settings[property] = value;
                break;
		}
    }
    initContactListener() {
        super.initContactListener();
        var self = this;
        this.contactListener.BeginContact = function (contact) {
        }
        this.contactListener.EndContact = function (contact) {
		}
		this.contactListener.PostSolve = function (contact, impulse) {
			if(!self.active && self.activateOn !== Explosive.activateOnTypes.none){
				var bodies = [contact.GetFixtureA().GetBody(), contact.GetFixtureB().GetBody()];
				var body;
				for (var i = 0; i < bodies.length; i++) {
					body = bodies[i];
					if(self.activateOn == Explosive.activateOnTypes.impact){
						const count = contact.GetManifold().get_pointCount();
						let force = 0;
						for (var j = 0; j < count; j++) force = Math.max(force, impulse.get_normalImpulses(j));
						force *= Math.min(body.GetMass(), 50);

						if(force > self.impactForExplosion){
							self.set('active', true);
						}
					}else if(self.activateOn == Explosive.activateOnTypes.mainCharacter){
						if(body.mainCharacter){
							self.set('active', true);
						}
					}else if(self.activateOn == Explosive.activateOnTypes.anyCharacter){
						if(body.mainCharacter){
							self.set('active', true);
						}
					}

				}
			}
		}
	}
	setActive(bool){
		this.active = bool;
		// override me
	}
}
Explosive.activateOnTypes = {
	none:"None",
	impact:"Impact",
	mainCharacter:"Main Character",
	anyCharacter:"Any Character"
}
Explosive.settings = Object.assign({}, Explosive.settings, {
    "delay": 3,
	"force": 2500,
	"active": false,
	"activateOn": "None",
});
Explosive.settingsOptions = Object.assign({}, Explosive.settingsOptions, {
    "delay": {
        min: 0.0,
        max: 10.0,
        step: 0.1
    },
    "force": {
        min: 100,
        max: 10000,
        step: 100
	},
	"active": false,
	"activateOn":Object.values(Explosive.activateOnTypes),
});

const getBodies = Object.assign(new Box2D.JSQueryCallback(), {
	bodies: [],
	bodyPointers: new Set(),
	ReportFixture: function(fixturePtr){
		const fixture = Box2D.wrapPointer( fixturePtr, Box2D.b2Fixture );
		const body = fixture.GetBody();
		const bodyPointer = Box2D.getPointer(body);
		if(this.bodyPointers.has(bodyPointer)) return true;
		this.bodyPointers.add(bodyPointer);

		if(body.GetType() === Box2D.b2_staticBody) return true;
		if(body.isPortalGhost || body.isPortalShadow) return true;
		if(body.mySprite && body.mySprite.isPhysicsParticleSprite) return true;
		if(body.mySprite?.data?.type === game.editor.object_TRIGGER) return true;

		const bodyClass = game.editor.retrieveClassFromBody(body);
		if(bodyClass){
			if(bodyClass.isExplosive && bodyClass.exploded) return true;
			if(bodyClass.isParticle) return true;
		}
		this.bodies.push(body);
		return true;
	},
	clean: function(){
		this.bodies.length = 0;
		this.bodyPointers.clear();
	}
});

const rayCallback = Object.assign(new Box2D.JSRayCastCallback(), {
	targetPointer: 0,
	m_fraction: 1,
	m_blockerFraction: 1,
	m_point: new Box2D.b2Vec2(),
	m_hit: false,
	reset: function(body){
		this.targetPointer = Box2D.getPointer(body);
		this.m_fraction = 1;
		this.m_blockerFraction = 1;
		this.m_hit = false;
	},
	ReportFixture: function (fixture_p,	point_p, normal, fraction) {

		const fixture = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture);
		const point = Box2D.wrapPointer(point_p, Box2D.b2Vec2);
		const body = fixture.GetBody();
		if(Box2D.getPointer(body) === this.targetPointer){
			this.m_hit = true;
			this.m_point.Set(point.x, point.y);
			this.m_fraction = fraction;
			return fraction;
		}

		if(body.GetType() !== Box2D.b2_staticBody || fixture.IsSensor()) return -1;
		this.m_blockerFraction = Math.min(this.m_blockerFraction, fraction);
		return fraction;
	}
});
